import { Component, inject, OnInit, signal } from '@angular/core';
import { StoresService } from '../../../core/services/stores.service';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { InvoicingService } from '../../../core/services/invoicing.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { concatMap, finalize, from, toArray } from 'rxjs';
import { ToastNotificationsService } from '../../../core/services/toast-notifications.service';
import { BsModalService } from 'ngx-bootstrap/modal';
import { DatePipe } from '@angular/common';
import { DropdownInputV2Component } from '../../../shared-with-client/dropdown-input-v2/dropdown-input-v2.component';
import { MerchantCurrencyPipe } from '../../../shared/pipes/merchant-currency.pipe';
import { ButtonComponent } from '../../../shared-with-client/button/button.component';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { NewModalBodyComponent } from "../../../shared/new-modal-body/new-modal-body.component";

@Component({
  selector: 'app-edit-payments-modal',
  imports: [ReactiveFormsModule, TranslatePipe, DatePipe, DropdownInputV2Component, MerchantCurrencyPipe, ButtonComponent, TranslatePipe, NewModalBodyComponent],
  templateUrl: './edit-payments-modal.component.html',
  styleUrl: './edit-payments-modal.component.scss'
})
export class EditPaymentsModalComponent implements OnInit {
    private storesService = inject(StoresService);
    private formBuilder = inject(FormBuilder);
    private modalService = inject(BsModalService);
    private invoicingService = inject(InvoicingService);
    private toastNotificationsService = inject(ToastNotificationsService);
    private translateService = inject(TranslateService);

    public currentStore = toSignal(this.storesService.getActiveStore());

    public dialogRef = inject(DialogRef<any>);
    public data = inject(DIALOG_DATA) as { payments: any; saleId: any };

    public submitAttempted = signal<any>(false);
    public form!: FormGroup;

    public loading = signal<boolean>(true);
    public saving = signal<boolean>(false);

    public paymentMethods = this.invoicingService.paymentMethods.filter(pm => !pm.isOnlinePayment);

    get paymentsArray(): FormArray {
        return this.form.get('payments') as FormArray;
    }

    get paymentsArrayHasEnableFields(): boolean {
        return this.paymentsArray.controls.some(control => control.get('paymentMethod')?.enabled);
    }

    ngOnInit(): void {
        this.setupForm(this.data.payments);
    }

    setupForm(payments?: any[]) {
        this.form = this.formBuilder.group({
            payments: this.formBuilder.array(
                payments && payments.length > 0
                    ? payments.map((payment) =>
                      {
                            const paymentMethod = payment.paymentMethod ? this.paymentMethods.find((pm: any) => pm.value === payment.paymentMethod && pm.isOnlinePayment === payment.isOnlinePayment) : null;
                            return this.formBuilder.group({
                              id: [payment.id],
                              paymentMethod: [{value: paymentMethod, disabled: payment.isOnlinePayment}, [Validators.required]],
                          })}
                          ,
                      )
                    : [],
            ),
        });

        this.loading.set(false);
    }

    submit() {
        this.submitAttempted.set(true);

        if (this.form.invalid) {
            return;
        }

        this.saving.set(true);

        from(this.form.value.payments).pipe(
            concatMap((paymentData: any) =>{
                paymentData.paymentMethod = paymentData.paymentMethod.value;
                return this.invoicingService.updateSalePayment(this.data.saleId, this.currentStore().id, paymentData.id, paymentData);
            }),
            toArray(),
            finalize(() => this.saving.set(false))
        ).subscribe({
            next: (results: any[]) => {
                const lastResult = results[results.length - 1];
                this.toastNotificationsService.showToast(this.translateService.instant('GENERAL.savedSuccessfully'), 'success');
                this.dialogRef.close(lastResult);
            },
            error: (error) => {
                this.toastNotificationsService.showToast(
                    error.errMsg || this.translateService.instant('GENERAL.generalErrorMsg'),
                    'error',
                );
            },
        });
    }

    close() {
        this.dialogRef.close();
    }

}
